import React, {useState, useCallback, useMemo, useRef} from 'react';
import {View, StyleSheet, FlatList, RefreshControl, ScrollView, TouchableOpacity, Platform, Modal} from 'react-native';
import {Text, IconButton, Menu, Dialog, Portal, Button, Chip, TextInput} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useFocusEffect} from '@react-navigation/native';
import DateTimePicker from '@react-native-community/datetimepicker';
import {colors, spacing} from '../../theme';
import {workAssignmentApi} from '../../services/api/workAssignmentApi';
import {workerApi} from '../../services/api/workerApi';
import WorkAssignmentStatusDialog from '../order/WorkAssignmentStatusDialog';

const STATUSES = ['NOT_STARTED', 'STARTED', 'COMPLETED'];
const STATUS_LABELS = {
  NOT_STARTED: 'Not Started',
  STARTED: 'Started',
  COMPLETED: 'Completed',
};

const PAYMENT_OPTIONS = [
  {value: false, label: 'Unpaid'},
  {value: true, label: 'Paid'},
];

const getDefaultStartDate = () => {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  d.setHours(0, 0, 0, 0);
  return d;
};

const getDefaultEndDate = () => {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d;
};

const DressAssignmentsScreen = () => {
  const {t} = useTranslation();
  const [assignments, setAssignments] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [menuVisibleId, setMenuVisibleId] = useState(null);
  const [statusDialogVisible, setStatusDialogVisible] = useState(false);
  const [statusAssignment, setStatusAssignment] = useState(null);

  // Filter state — default to last 1 week
  const [filterVisible, setFilterVisible] = useState(false);
  const [filterWorkerId, setFilterWorkerId] = useState(null);
  const [filterStatus, setFilterStatus] = useState(null);
  const [filterPaid, setFilterPaid] = useState(null); // null=all, true=paid, false=unpaid
  const [filterStartDate, setFilterStartDate] = useState(getDefaultStartDate);
  const [filterEndDate, setFilterEndDate] = useState(getDefaultEndDate);
  const [filterOrderId, setFilterOrderId] = useState('');

  // Temp filter state (edited in modal, applied on "Apply")
  const [tmpWorkerId, setTmpWorkerId] = useState(null);
  const [tmpStatus, setTmpStatus] = useState(null);
  const [tmpPaid, setTmpPaid] = useState(null);
  const [tmpStartDate, setTmpStartDate] = useState(getDefaultStartDate);
  const [tmpEndDate, setTmpEndDate] = useState(getDefaultEndDate);
  const [tmpOrderId, setTmpOrderId] = useState('');

  // Date picker — full-screen modal approach to avoid z-index issues
  const [datePickerVisible, setDatePickerVisible] = useState(false);
  const [datePickerTarget, setDatePickerTarget] = useState(null); // 'start' | 'end'
  const [datePickerValue, setDatePickerValue] = useState(new Date());

  const hasFilters = filterWorkerId || filterStatus || filterPaid !== null || filterStartDate || filterEndDate || filterOrderId;

  const workerMap = useMemo(() => {
    const map = {};
    workers.forEach(w => { map[w.id] = w.fullName; });
    return map;
  }, [workers]);

  const buildParams = useCallback((wId, st, pd, sd, ed, oid) => {
    const params = {};
    if (wId) params.workerId = wId;
    if (st) params.status = st;
    if (pd !== null) params.paid = pd;
    if (sd) params.startDate = formatDateISO(sd);
    if (ed) params.endDate = formatDateISO(ed);
    if (oid) params.orderId = oid;
    return Object.keys(params).length > 0 ? params : undefined;
  }, []);

  const fetchAssignments = useCallback(async () => {
    try {
      const params = buildParams(filterWorkerId, filterStatus, filterPaid, filterStartDate, filterEndDate, filterOrderId);
      const res = await workAssignmentApi.getAll(params);
      setAssignments(res.data?.data || []);
    } catch (error) {
      // silently fail on refresh
    }
  }, [filterWorkerId, filterStatus, filterPaid, filterStartDate, filterEndDate, filterOrderId, buildParams]);

  const fetchWorkers = useCallback(async () => {
    try {
      const res = await workerApi.search({page: 0, size: 200, isActive: true});
      setWorkers(res.data?.data?.content || []);
    } catch (e) {
      // silent
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      fetchAssignments();
      fetchWorkers();
    }, [fetchAssignments, fetchWorkers]),
  );

  const onRefresh = async () => {
    setRefreshing(true);
    await fetchAssignments();
    setRefreshing(false);
  };

  const openFilterModal = useCallback(() => {
    setTmpWorkerId(filterWorkerId);
    setTmpStatus(filterStatus);
    setTmpPaid(filterPaid);
    setTmpStartDate(filterStartDate);
    setTmpEndDate(filterEndDate);
    setTmpOrderId(filterOrderId);
    setFilterVisible(true);
  }, [filterWorkerId, filterStatus, filterPaid, filterStartDate, filterEndDate, filterOrderId]);

  const applyFilters = useCallback(() => {
    setFilterWorkerId(tmpWorkerId);
    setFilterStatus(tmpStatus);
    setFilterPaid(tmpPaid);
    setFilterStartDate(tmpStartDate);
    setFilterEndDate(tmpEndDate);
    setFilterOrderId(tmpOrderId);
    setFilterVisible(false);
  }, [tmpWorkerId, tmpStatus, tmpPaid, tmpStartDate, tmpEndDate, tmpOrderId]);

  const clearFilters = useCallback(() => {
    setFilterWorkerId(null);
    setFilterStatus(null);
    setFilterPaid(null);
    setFilterStartDate(null);
    setFilterEndDate(null);
    setFilterOrderId('');
    // Also clear tmp state
    setTmpWorkerId(null);
    setTmpStatus(null);
    setTmpPaid(null);
    setTmpStartDate(null);
    setTmpEndDate(null);
    setTmpOrderId('');
    setFilterVisible(false);
  }, []);

  // Open date picker: close filter modal, show date picker in its own Modal
  const openDatePicker = useCallback((target) => {
    const currentVal = target === 'start' ? tmpStartDate : tmpEndDate;
    setDatePickerTarget(target);
    setDatePickerValue(currentVal || new Date());
    setFilterVisible(false);
    // Small delay to let dialog dismiss before showing picker modal
    setTimeout(() => setDatePickerVisible(true), 300);
  }, [tmpStartDate, tmpEndDate]);

  const handleDatePickerConfirm = useCallback(() => {
    if (datePickerTarget === 'start') setTmpStartDate(datePickerValue);
    else if (datePickerTarget === 'end') setTmpEndDate(datePickerValue);
    setDatePickerVisible(false);
    setTimeout(() => setFilterVisible(true), 300);
  }, [datePickerTarget, datePickerValue]);

  const handleDatePickerCancel = useCallback(() => {
    setDatePickerVisible(false);
    setTimeout(() => setFilterVisible(true), 300);
  }, []);

  const handleAndroidDateChange = useCallback((event, date) => {
    if (event.type === 'dismissed') {
      setDatePickerVisible(false);
      setTimeout(() => setFilterVisible(true), 300);
      return;
    }
    if (date) {
      if (datePickerTarget === 'start') setTmpStartDate(date);
      else if (datePickerTarget === 'end') setTmpEndDate(date);
    }
    setDatePickerVisible(false);
    setTimeout(() => setFilterVisible(true), 300);
  }, [datePickerTarget]);

  const formatDate = dateStr => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', {month: 'short', day: 'numeric'});
  };

  const formatDateShort = date => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('en-IN', {month: 'short', day: 'numeric'});
  };

  const formatDateDisplay = date => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('en-IN', {year: 'numeric', month: 'short', day: 'numeric'});
  };

  const getStatusColor = status => {
    switch (status) {
      case 'COMPLETED': return colors.success;
      case 'STARTED': return colors.primary;
      case 'DELETED': return colors.error;
      default: return colors.textSecondary;
    }
  };

  const renderItem = ({item, index}) => (
    <View style={[styles.dataRow, index % 2 === 1 && styles.dataRowAlt]}>
      <View style={styles.nameCol}>
        <Text style={styles.nameText}>{item.workerName}</Text>
        {item.workerRole && <Text style={styles.roleText}>{item.workerRole}</Text>}
      </View>
      <View style={styles.itemCol}>
        <Text style={styles.cell}>{item.catalogItemName || '-'}</Text>
        <Text style={styles.codeText}>{item.instanceCode}</Text>
      </View>
      <Text style={[styles.cell, styles.rateCol]}>
        {item.pieceRate != null ? `\u20B9${Number(item.pieceRate)}` : '-'}
      </Text>
      <Text style={[styles.cell, styles.dateCol]}>{formatDate(item.assignedAt)}</Text>
      <Text style={[styles.cell, styles.statusCol, {color: getStatusColor(item.status)}]}>
        {item.status?.replace('_', ' ')}
      </Text>
      <View style={styles.moreCol}>
        <Menu
          visible={menuVisibleId === item.id}
          onDismiss={() => setMenuVisibleId(null)}
          anchor={
            <IconButton
              icon="dots-vertical"
              size={18}
              onPress={() => setMenuVisibleId(item.id)}
            />
          }>
          <Menu.Item
            title="Change Status"
            onPress={() => {
              setMenuVisibleId(null);
              setStatusAssignment(item);
              setStatusDialogVisible(true);
            }}
          />
        </Menu>
      </View>
    </View>
  );

  return (
    <View style={styles.container}>
      {/* Filter chips bar */}
      <View style={styles.chipBar}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.chipScroll}>
          {filterWorkerId && (
            <Chip style={styles.filterChip} textStyle={styles.filterChipText}
              onClose={() => setFilterWorkerId(null)}>
              {workerMap[filterWorkerId] || 'Worker'}
            </Chip>
          )}
          {filterStatus && (
            <Chip style={styles.filterChip} textStyle={styles.filterChipText}
              onClose={() => setFilterStatus(null)}>
              {STATUS_LABELS[filterStatus]}
            </Chip>
          )}
          {filterPaid !== null && (
            <Chip style={styles.filterChip} textStyle={styles.filterChipText}
              onClose={() => setFilterPaid(null)}>
              {filterPaid ? 'Paid' : 'Unpaid'}
            </Chip>
          )}
          {filterStartDate && (
            <Chip style={styles.filterChip} textStyle={styles.filterChipText}
              onClose={() => setFilterStartDate(null)}>
              From: {formatDateShort(filterStartDate)}
            </Chip>
          )}
          {filterEndDate && (
            <Chip style={styles.filterChip} textStyle={styles.filterChipText}
              onClose={() => setFilterEndDate(null)}>
              To: {formatDateShort(filterEndDate)}
            </Chip>
          )}
          {filterOrderId ? (
            <Chip style={styles.filterChip} textStyle={styles.filterChipText}
              onClose={() => setFilterOrderId('')}>
              Order: {filterOrderId}
            </Chip>
          ) : null}
          {hasFilters && (
            <TouchableOpacity onPress={clearFilters} style={styles.clearBtn}>
              <Text style={styles.clearText}>Clear</Text>
            </TouchableOpacity>
          )}
        </ScrollView>
        <IconButton
          icon={hasFilters ? 'filter-check' : 'filter-variant'}
          size={22}
          iconColor={hasFilters ? colors.primary : colors.textSecondary}
          onPress={openFilterModal}
          style={styles.filterIconBtn}
        />
      </View>

      {/* Table header */}
      <View style={styles.tableHeader}>
        <Text style={[styles.headerCell, styles.nameCol]}>Worker</Text>
        <Text style={[styles.headerCell, styles.itemCol]}>Item</Text>
        <Text style={[styles.headerCell, styles.rateCol]}>Rate</Text>
        <Text style={[styles.headerCell, styles.dateCol]}>Date</Text>
        <Text style={[styles.headerCell, styles.statusCol]}>Status</Text>
        <Text style={[styles.headerCell, styles.moreCol]}>...</Text>
      </View>

      <FlatList
        data={assignments}
        keyExtractor={item => String(item.id)}
        renderItem={renderItem}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={[colors.primary]} />
        }
        ListEmptyComponent={
          <Text style={styles.emptyText}>{t('common.noData')}</Text>
        }
      />

      {statusAssignment && (
        <WorkAssignmentStatusDialog
          visible={statusDialogVisible}
          onDismiss={() => setStatusDialogVisible(false)}
          assignment={statusAssignment}
          onStatusChanged={fetchAssignments}
        />
      )}

      {/* Filter Modal */}
      <Portal>
        <Dialog visible={filterVisible} onDismiss={() => setFilterVisible(false)} style={{borderRadius: 15}}>
          <View style={styles.dialogTitleRow}>
            <Text variant="titleLarge" style={{fontWeight: '600'}}>Filter Assignments</Text>
            <IconButton icon="close" size={22} onPress={() => setFilterVisible(false)} style={{margin: 0}} />
          </View>
          <Dialog.ScrollArea style={{maxHeight: 460, paddingHorizontal: 0}}>
            <ScrollView>
              <View style={styles.filterSection}>
                {/* Worker */}
                <Text variant="labelLarge" style={styles.filterLabel}>Worker</Text>
                <View style={styles.filterListBox}>
                  <ScrollView nestedScrollEnabled>
                    {workers.map(w => {
                      const selected = tmpWorkerId === w.id;
                      return (
                        <TouchableOpacity
                          key={w.id}
                          style={[styles.filterListItem, selected && styles.filterListItemSelected]}
                          onPress={() => setTmpWorkerId(selected ? null : w.id)}
                          activeOpacity={0.7}>
                          <Text style={[styles.filterListItemText, selected && styles.filterListItemTextSelected]}>
                            {w.fullName}
                          </Text>
                          {selected && <IconButton icon="check" size={16} iconColor={colors.primary} style={{margin: 0, padding: 0, width: 20, height: 20}} />}
                        </TouchableOpacity>
                      );
                    })}
                  </ScrollView>
                </View>

                {/* Date range */}
                <Text variant="labelLarge" style={styles.filterLabel}>Assignment Date</Text>
                <View style={styles.dateRow}>
                  <TouchableOpacity style={styles.dateInput} onPress={() => openDatePicker('start')}>
                    <IconButton icon="calendar" size={16} iconColor={colors.primary} style={{margin: 0, padding: 0, width: 20, height: 20}} />
                    <Text style={tmpStartDate ? styles.dateText : styles.datePlaceholder}>
                      {tmpStartDate ? formatDateDisplay(tmpStartDate) : 'Start date'}
                    </Text>
                    {tmpStartDate && (
                      <IconButton icon="close" size={14} onPress={(e) => { e?.stopPropagation?.(); setTmpStartDate(null); }} style={{margin: 0, padding: 0, width: 20, height: 20}} />
                    )}
                  </TouchableOpacity>
                  <Text style={styles.dateSep}>to</Text>
                  <TouchableOpacity style={styles.dateInput} onPress={() => openDatePicker('end')}>
                    <IconButton icon="calendar" size={16} iconColor={colors.primary} style={{margin: 0, padding: 0, width: 20, height: 20}} />
                    <Text style={tmpEndDate ? styles.dateText : styles.datePlaceholder}>
                      {tmpEndDate ? formatDateDisplay(tmpEndDate) : 'End date'}
                    </Text>
                    {tmpEndDate && (
                      <IconButton icon="close" size={14} onPress={(e) => { e?.stopPropagation?.(); setTmpEndDate(null); }} style={{margin: 0, padding: 0, width: 20, height: 20}} />
                    )}
                  </TouchableOpacity>
                </View>

                {/* Status */}
                <Text variant="labelLarge" style={styles.filterLabel}>Status</Text>
                <View style={styles.filterChipGroup}>
                  {STATUSES.map(s => (
                    <Chip key={s} selected={tmpStatus === s}
                      onPress={() => setTmpStatus(tmpStatus === s ? null : s)}
                      style={styles.filterSelectChip} textStyle={{fontSize: 13}}>
                      {STATUS_LABELS[s]}
                    </Chip>
                  ))}
                </View>

                {/* Payment status */}
                <Text variant="labelLarge" style={styles.filterLabel}>Payment Status</Text>
                <View style={styles.filterChipGroup}>
                  {PAYMENT_OPTIONS.map(p => (
                    <Chip key={String(p.value)} selected={tmpPaid === p.value}
                      onPress={() => setTmpPaid(tmpPaid === p.value ? null : p.value)}
                      style={styles.filterSelectChip} textStyle={{fontSize: 13}}>
                      {p.label}
                    </Chip>
                  ))}
                </View>

                {/* Order ID */}
                <Text variant="labelLarge" style={styles.filterLabel}>Order ID</Text>
                <TextInput
                  mode="outlined"
                  placeholder="Enter order ID"
                  value={tmpOrderId}
                  onChangeText={setTmpOrderId}
                  keyboardType="numeric"
                  dense
                  style={styles.orderIdInput}
                />
              </View>
            </ScrollView>
          </Dialog.ScrollArea>
          <Dialog.Actions>
            <Button onPress={clearFilters}>Clear Filters</Button>
            <Button mode="contained" onPress={applyFilters}
              style={{borderRadius: 10}} buttonColor={colors.primary}>
              Apply Filters
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>

      {/* Date picker — shown in a separate full-screen Modal on iOS, native dialog on Android */}
      {Platform.OS === 'ios' ? (
        <Modal visible={datePickerVisible} transparent animationType="slide">
          <View style={styles.datePickerOverlay}>
            <View style={styles.datePickerSheet}>
              <View style={styles.datePickerHeader}>
                <Button onPress={handleDatePickerCancel}>Cancel</Button>
                <Text variant="titleMedium" style={{fontWeight: '600'}}>
                  {datePickerTarget === 'start' ? 'Start Date' : 'End Date'}
                </Text>
                <Button onPress={handleDatePickerConfirm}>Done</Button>
              </View>
              <DateTimePicker
                value={datePickerValue}
                mode="date"
                display="spinner"
                onChange={(event, date) => { if (date) setDatePickerValue(date); }}
                style={{height: 200}}
              />
            </View>
          </View>
        </Modal>
      ) : (
        datePickerVisible && (
          <DateTimePicker
            value={datePickerValue}
            mode="date"
            display="default"
            onChange={handleAndroidDateChange}
          />
        )
      )}
    </View>
  );
};

const formatDateISO = date => {
  if (!date) return null;
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.surface},
  chipBar: {
    flexDirection: 'row', alignItems: 'center',
    paddingLeft: spacing.sm, backgroundColor: colors.surface,
    borderBottomWidth: 1, borderBottomColor: colors.divider,
  },
  chipScroll: {
    flexDirection: 'row', alignItems: 'center',
    paddingVertical: spacing.xs, gap: spacing.xs,
  },
  filterChip: {backgroundColor: colors.primary + '20', height: 30},
  filterChipText: {fontSize: 12},
  clearBtn: {justifyContent: 'center', paddingHorizontal: spacing.xs},
  clearText: {color: colors.primary, fontSize: 12, fontWeight: '600'},
  filterIconBtn: {margin: 0},
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: colors.primary,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.xs,
    alignItems: 'center',
  },
  headerCell: {
    fontSize: 11,
    fontWeight: '700',
    color: colors.white,
    textAlign: 'center',
  },
  nameCol: {flex: 2},
  itemCol: {flex: 2},
  rateCol: {flex: 1.2, textAlign: 'center'},
  dateCol: {flex: 1.2, textAlign: 'center'},
  statusCol: {flex: 1.5, textAlign: 'center'},
  moreCol: {width: 36, alignItems: 'center'},
  dataRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.xs,
    backgroundColor: colors.surface,
    borderBottomWidth: 1,
    borderBottomColor: colors.divider,
  },
  dataRowAlt: {backgroundColor: colors.background},
  cell: {fontSize: 12, color: colors.text, textAlign: 'center'},
  nameText: {fontSize: 12, fontWeight: '600', color: colors.text},
  roleText: {fontSize: 10, color: colors.textSecondary},
  codeText: {fontSize: 10, color: colors.textSecondary},
  emptyText: {
    textAlign: 'center',
    color: colors.textSecondary,
    marginTop: spacing.xxl,
    fontSize: 16,
  },
  // Filter modal styles
  dialogTitleRow: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    paddingHorizontal: spacing.lg, paddingTop: spacing.md, paddingBottom: spacing.xs,
  },
  filterSection: {paddingHorizontal: spacing.lg, paddingVertical: spacing.sm},
  filterLabel: {fontWeight: '600', color: colors.text, marginTop: spacing.md, marginBottom: spacing.xs},
  filterChipGroup: {flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs},
  filterSelectChip: {backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border},
  filterListBox: {
    borderWidth: 1, borderColor: colors.border, borderRadius: 8,
    backgroundColor: colors.surface, maxHeight: 100, overflow: 'hidden',
  },
  filterListItem: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingVertical: 5, paddingHorizontal: 12,
    borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: colors.divider,
  },
  filterListItemSelected: {backgroundColor: colors.primary + '12'},
  filterListItemText: {fontSize: 14, color: colors.text},
  filterListItemTextSelected: {fontWeight: '600', color: colors.primary},
  dateRow: {flexDirection: 'row', alignItems: 'center', gap: spacing.xs},
  dateInput: {
    flex: 1, flexDirection: 'row', alignItems: 'center', gap: 4,
    borderWidth: 1, borderColor: colors.border, borderRadius: 8,
    backgroundColor: colors.surface, paddingHorizontal: 6, paddingVertical: 8,
  },
  dateText: {fontSize: 12, color: colors.text, flex: 1},
  datePlaceholder: {fontSize: 12, color: colors.textSecondary, flex: 1},
  dateSep: {fontSize: 13, color: colors.textSecondary},
  orderIdInput: {backgroundColor: colors.surface, fontSize: 14},
  // Date picker modal styles (iOS)
  datePickerOverlay: {
    flex: 1, justifyContent: 'flex-end', backgroundColor: 'rgba(0,0,0,0.4)',
  },
  datePickerSheet: {
    backgroundColor: colors.surface, borderTopLeftRadius: 16, borderTopRightRadius: 16,
    paddingBottom: 30,
  },
  datePickerHeader: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    paddingHorizontal: spacing.sm, paddingVertical: spacing.xs,
    borderBottomWidth: 1, borderBottomColor: colors.divider,
  },
});

export default DressAssignmentsScreen;
